(function ammCartDrawerRuntime() {
  if (window.__ammCartDrawerEditorialLoaded) return;
  window.__ammCartDrawerEditorialLoaded = true;

  const getRoutesRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || "/";
  };

  const parseStoredIds = (key) => {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(key) || "[]");
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
      return [];
    }
  };

  const storeIds = (key, ids) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(ids));
    } catch (error) {
      // A blocked session store should not prevent recommendations from rendering.
    }
  };

  class AmmCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    async fetchCards(url) {
      const response = await fetch(url, {
        credentials: "same-origin",
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Recommendation request failed with ${response.status}`);
      }

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, "text/html");
      return Array.from(documentFragment.querySelectorAll("[data-amm-upsell-card]"));
    }

    selectCards(cards) {
      const excluded = new Set(
        (this.dataset.excludedProducts || "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean)
      );
      const uniqueCards = new Map();

      cards.forEach((card) => {
        const productId = String(card.dataset.productId || "");
        if (productId && !excluded.has(productId) && !uniqueCards.has(productId)) {
          uniqueCards.set(productId, card);
        }
      });

      const availableCards = Array.from(uniqueCards.values());
      const sessionKey = this.dataset.sessionKey || "amm-cart-upsell";
      const storedIds = parseStoredIds(sessionKey);
      const selected = [];

      storedIds.forEach((productId) => {
        const card = uniqueCards.get(productId);
        if (card && selected.length < 2) selected.push(card);
      });

      const unusedCards = availableCards.filter((card) => !selected.includes(card));
      for (let index = unusedCards.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [unusedCards[index], unusedCards[randomIndex]] = [unusedCards[randomIndex], unusedCards[index]];
      }

      selected.push(...unusedCards.slice(0, Math.max(0, 2 - selected.length)));
      storeIds(
        sessionKey,
        selected.map((card) => String(card.dataset.productId))
      );

      return selected;
    }

    async load() {
      if (this.dataset.loaded === "true" || this.dataset.loading === "true") return;

      const list = this.querySelector(".amm-upsell__list");
      const recommendationsUrl = this.dataset.url;
      if (!list || !recommendationsUrl) {
        this.hidden = true;
        return;
      }

      this.dataset.loading = "true";
      this.abortController = new AbortController();

      try {
        let cards = [];

        try {
          cards = await this.fetchCards(recommendationsUrl);
        } catch (primaryError) {
          if (primaryError.name === "AbortError") throw primaryError;
        }

        let selectedCards = this.selectCards(cards);

        if (selectedCards.length < 2) {
          const separator = getRoutesRoot().includes("?") ? "&" : "?";
          const fallbackUrl = `${getRoutesRoot()}${separator}section_id=amm-cart-recommendations`;
          const fallbackCards = await this.fetchCards(fallbackUrl);
          selectedCards = this.selectCards([...cards, ...fallbackCards]);
        }

        if (selectedCards.length === 0) {
          this.hidden = true;
          return;
        }

        list.replaceChildren(...selectedCards.map((card) => card.cloneNode(true)));
        list.setAttribute("aria-busy", "false");
        this.dataset.loaded = "true";
      } catch (error) {
        if (error.name !== "AbortError") {
          this.hidden = true;
          console.warn("A-Ma-Roam cart recommendations could not be loaded.", error);
        }
      } finally {
        delete this.dataset.loading;
      }
    }
  }

  if (!customElements.get("amm-cart-recommendations")) {
    customElements.define("amm-cart-recommendations", AmmCartRecommendations);
  }

  document.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-amm-upsell-add]");
    if (!button || button.getAttribute("aria-disabled") === "true") return;

    event.preventDefault();

    const variantId = Number(button.dataset.variantId);
    if (!Number.isFinite(variantId)) return;

    const defaultLabel = button.dataset.defaultLabel || button.textContent;
    button.setAttribute("aria-disabled", "true");
    button.textContent = button.dataset.loadingLabel || defaultLabel;

    try {
      const response = await fetch(`${getRoutesRoot()}cart/add.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || payload.message || "Unable to add this item.");
      }

      document.dispatchEvent(
        new CustomEvent("cart:refresh", {
          detail: { open: true, source: "amm-cart-upsell" },
        })
      );
    } catch (error) {
      button.textContent = button.dataset.errorLabel || defaultLabel;
      console.warn("A-Ma-Roam upsell item could not be added.", error);

      window.setTimeout(() => {
        if (button.isConnected) button.textContent = defaultLabel;
      }, 1800);
    } finally {
      if (button.isConnected) {
        button.removeAttribute("aria-disabled");
        if (button.textContent !== button.dataset.errorLabel) button.textContent = defaultLabel;
      }
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    const drawer = document.querySelector("m-cart-drawer.m-cart-drawer--active");
    if (drawer && typeof drawer.close === "function") drawer.close();
  });
})();
